/**
 * The GRN repair drag board for one PO line — schedule drop zones, GRN cards,
 * the unallocated bucket, and the split / cancel / un-cancel / qty modals.
 *
 * Rendered by the GRN Allocation Repair page and by the Import Monitor's
 * ERP-mismatch repair modal, both driving it with ``useGrnRepairLine``.
 */
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import CloseHeldScheduleDialog from '../../components/purchasing/CloseHeldScheduleDialog';
import { useState, type ReactNode } from 'react';
import type { RepairSchedule } from '../../lib/api/grn-repair';
import { useAuth } from '../../contexts/AuthContext';
import { GrnCard, DraggableGrn, ScheduleDropZone, isArrearSchedule, localTodayIso } from './GrnRepairCards';
import { SplitModal, CancelScheduleModal, UncancelScheduleModal, QtyEditModal } from './GrnRepairModals';
import type { GrnRepairLine, RepairSlot } from './useGrnRepairLine';

interface GrnRepairBoardProps {
  line: GrnRepairLine;
  /** Rendered above the schedules, inside the drag context. */
  header?: ReactNode;
  /** Show each slot's ERP balance beside Helix's. */
  showErp?: boolean;
  /** Mark true-arrear slots (date passed, balance still open). */
  highlightArrears?: boolean;
}

/** GRN Repair's closest-match rebalance for the open line. */
export function ClosestMatchButton({ line }: { line: GrnRepairLine }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={line.performRebalance}
      disabled={line.rebalancing !== null || line.detailLoading}
      title="Walk GRNs in receipt order; land each whole onto its closest eligible schedule (1d early → 5d late). Never splits. Line-0 receipts untouched."
    >
      {line.rebalancing === 'closest' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
      Closest-match
    </Button>
  );
}

export default function GrnRepairBoard({ line, header, showErp = false, highlightArrears = false }: GrnRepairBoardProps) {
  const {
    detail, sensors, slotsBySched, activeDragGrn, onDragStart, onDragEnd,
    performSlotMove, performDelete, splitTarget, setSplitTarget, splitQty, setSplitQty, performSplit,
    cancelTarget, setCancelTarget, cancelling, performCancelSchedule,
    uncancelTarget, setUncancelTarget, uncancelling, performUncancelSchedule,
    qtyTarget, setQtyTarget, savingQty, performSetQty,
    refreshOpenLine,
  } = line;
  const [closeHeldTarget, setCloseHeldTarget] = useState<RepairSchedule | null>(null);
  // Close-held is customer-side only (the server refuses a supplier). The
  // board itself is gated on staff/superuser, which says nothing about SIDE,
  // so the button is offered on the same rule the Scheduled Lines row uses.
  const { user } = useAuth();
  const canCloseHeld = user?.platform === 'CUSTOMER' || user?.platform === 'HELIX';
  if (!detail) return null;

  const card = (slot: RepairSlot, schedDate: string | null, currentSchedId: number | null) => (
    <DraggableGrn
      key={slot.key}
      dragId={slot.key}
      grn={slot.grn}
      qtyOnSlot={slot.qty}
      isOverDelivery={slot.isOverDelivery}
      schedDate={schedDate}
      win={detail.otif_window}
      schedules={detail.schedules}
      onMove={(sid) => performSlotMove(slot, sid)}
      onSplit={slot.allocationId !== null ? () => { setSplitTarget(slot); setSplitQty(''); } : undefined}
      onDelete={slot.allocationId !== null ? () => performDelete(slot) : undefined}
      canSplit={slot.allocationId !== null && parseFloat(slot.qty) > 0}
      currentSchedId={currentSchedId}
    />
  );
  const unassigned = slotsBySched.get('unassigned') || [];
  const todayIso = localTodayIso();

  return (
    <>
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        {header}
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
          <CheckCircle2 className="w-3 h-3" /> Drag GRN cards between schedule rows. Each drop saves immediately.
        </div>
        {detail.schedules
          .map((s) => (
            <ScheduleDropZone key={s.id} sched={s} showErp={showErp}
              highlightArrear={highlightArrears && isArrearSchedule(s, todayIso)}
              onCancel={() => setCancelTarget(s)} onUncancel={() => setUncancelTarget(s)} onEditQty={() => setQtyTarget(s)}
              onCloseHeld={canCloseHeld ? () => setCloseHeldTarget(s) : undefined}>
              {(slotsBySched.get(s.id) || []).map((slot) => card(slot, s.schedule_date, s.id))}
            </ScheduleDropZone>
          ))}
        {unassigned.length > 0 && (
          <div className="border-dashed border rounded p-3 mt-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Unallocated (no schedule FK)</div>
            <div className="flex flex-wrap gap-2">{unassigned.map((slot) => card(slot, null, null))}</div>
          </div>
        )}
        <DragOverlay>
          {activeDragGrn ? <GrnCard grn={activeDragGrn} schedDate={null} win={detail.otif_window} /> : null}
        </DragOverlay>
      </DndContext>

      {splitTarget && (
        <SplitModal
          grnId={splitTarget.grn.id}
          currentQty={splitTarget.qty}
          splitQty={splitQty}
          onQtyChange={setSplitQty}
          onClose={() => setSplitTarget(null)}
          onSplit={performSplit}
        />
      )}
      {cancelTarget && (
        <CancelScheduleModal
          lineNumber={cancelTarget.schedule_line_number}
          schedDate={cancelTarget.schedule_date}
          cancelling={cancelling}
          onClose={() => setCancelTarget(null)}
          onConfirm={() => performCancelSchedule(detail.schedules)}
        />
      )}
      {uncancelTarget && (
        <UncancelScheduleModal
          lineNumber={uncancelTarget.schedule_line_number}
          schedDate={uncancelTarget.schedule_date}
          uncancelling={uncancelling}
          onClose={() => setUncancelTarget(null)}
          onConfirm={performUncancelSchedule}
        />
      )}
      <CloseHeldScheduleDialog
        open={closeHeldTarget !== null}
        schedule={closeHeldTarget ? {
          ...closeHeldTarget,
          po_number: detail.po_line.po_number,
          line_number: Number(detail.po_line.line_number),
          part_number: detail.po_line.part_number,
        } : null}
        onClose={() => setCloseHeldTarget(null)}
        onDone={refreshOpenLine}
      />
      {qtyTarget && (
        <QtyEditModal
          lineNumber={qtyTarget.schedule_line_number}
          schedDate={qtyTarget.schedule_date}
          currentQty={qtyTarget.schedule_qty}
          saving={savingQty}
          onClose={() => setQtyTarget(null)}
          onSave={performSetQty}
        />
      )}
    </>
  );
}
